import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import FormRemoteSelectInput from 'components/FormRemoteSelectInput';
import InputNumber from 'components/InputNumber';
import useAuth from 'contexts/useAuth';
import React from 'react';
import { useForm } from 'react-hook-form';
import { createWithdrawal, getAccounts } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import CustomButton from 'components/CustomButton';

interface FormData {
  amount: number;
  accountId: string;
  description: string;
}

const CreateWithdrawel: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const toast = useToast();
  const { userData, updateUserData } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [accountId, setAccountId] = React.useState<string | undefined>();
  const [favored, setFavored] = React.useState<'DICT' | 'MANU'>();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormData>();

  const { requestTwoFa } = useTwoFaHelper();

  const createwithdrawel = async (data: FormData) => {
    const code = await requestTwoFa();
    if (!code?.id) {
      return;
    }

    setLoading(true);

    try {
      await createWithdrawal(code.id, {
        ...data,
        customerAccountId: accountId,
        type: favored,
      });
      updateUserData();
      toast({
        title: 'Sucesso',
        description: 'Saque criado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao criar saque',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Criar Saque</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack
            spacing={5}
            alignItems="start"
          >
            <Text fontWeight="semibold">Dados bancários</Text>

            <FormRemoteSelectInput
              control={control}
              label="Conta cadastrada?"
              onChange={(e: any) => {
                setAccountId(e?.value);
                setFavored(e.type);
              }}
              loadDataFn={({ value, cb }) =>
                getAccounts({ filter: value }).then((retorno) => {
                  if (retorno?.registers?.length > 0) {
                    cb([
                      { label: 'Escolher uma opção...', value: '' },
                      ...retorno.registers?.map((d: any) => ({
                        label: (d.accountHolder ?? '---') + ' - ' + (d.pix || d?.agency + ' - ' + d?.account),
                        value: d.id,
                        type: d.pix ? 'DICT' : 'MANU',
                      })),
                    ]);
                  } else {
                    cb([]);
                  }
                })
              }
              name="accountId"
            />

            <InputNumber
              control={control}
              percentage={false}
              placeholder="R$ 0,00"
              label={'Valor'}
              name={`amount`}
              rules={{
                required: 'Campo obrigatório',
                validate: {
                  positive: (value) => value > 0 || 'O valor deve ser maior que 0',
                  max: (value) =>
                    value <= +userData.balance - +userData.amountLocked ||
                    'O valor deve ser menor que o saldo disponível',
                },
              }}
            />
            <Text>
              Saldo Disponível: <b>{maskCurrency(userData.balance - +userData.amountLocked)}</b>
            </Text>
            <FormControl isInvalid={!!errors.description?.message}>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                mb="8px"
              >
                Descrição<Text>*</Text>
              </FormLabel>
              <Input
                isRequired={true}
                variant="auth"
                fontSize="sm"
                size="sm"
                {...register('description', {
                  maxLength: {
                    value: 100,
                    message: 'O limite de caracteres é de 100',
                  },
                })}
                ms={{ base: '0px', md: '0px' }}
                type="text"
                rounded="sm"
                placeholder="Pagamento de contas"
              />
              <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={4}>
            <CustomButton
              colorScheme="green"
              isLoading={loading}
              onClick={handleSubmit(createwithdrawel)}
            >
              Criar
            </CustomButton>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateWithdrawel;
